import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useUserProfile, type UserProfile } from '@/hooks/useUserProfile';

interface AccountMenuProps {
  onSignOut: () => void;
  /**
   * False on terms acceptance: the profile route would bounce straight back
   * there, so the menu offers only Sign out.
   */
  showProfileLink?: boolean;
}

/**
 * The single home for identity and sign-out, in the header on every page.
 *
 * Built as a disclosure (a button that shows and hides a panel) rather than an
 * ARIA role="menu". The menu role promises arrow-key navigation and an
 * application-style widget; a disclosure containing an ordinary link and
 * button is what WAI-ARIA recommends for this kind of account dropdown, and it
 * behaves the way people expect with Tab.
 */
export function AccountMenu({ onSignOut, showProfileLink = true }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const { data: profile } = useUserProfile();
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;

    // Move focus to the first action so keyboard users land in the panel.
    // After a mouse click this doesn't draw a focus ring, because the ring is
    // styled with :focus-visible.
    panelRef.current?.querySelector<HTMLElement>('a[href], button')?.focus();

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const displayName = profile?.name || profile?.email || '';
  const initials = getInitials(profile);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      // Close when Tab carries focus to something outside. relatedTarget is
      // null when focus goes nowhere in particular (and on Safari, clicking a
      // button doesn't focus it at all), so those cases are left to the
      // outside-pointerdown handler rather than closing the panel mid-click.
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (next && !wrapperRef.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={displayName ? `Account menu for ${displayName}` : 'Account menu'}
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-2 rounded-full p-1 text-slate-900 hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:rounded-md md:pr-2"
      >
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-white"
        >
          {initials || <PersonIcon />}
        </span>
        {/* Phones get the initials alone; there isn't room for a name. */}
        {displayName && (
          <span className="hidden max-w-[14rem] truncate text-sm font-medium md:inline">
            {displayName}
          </span>
        )}
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          // Focusable so a click on the name or email keeps focus inside the
          // wrapper instead of dropping it to <body>.
          tabIndex={-1}
          className="absolute top-full right-0 z-40 mt-2 w-72 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg focus:outline-none"
        >
          {(profile?.name || profile?.email) && (
            <div className="border-b border-slate-100 px-4 py-3">
              {profile.name && (
                <p className="truncate font-semibold text-slate-900">{profile.name}</p>
              )}
              {profile.email && (
                <p className="truncate text-sm text-gray1" title={profile.email}>
                  {profile.email}
                </p>
              )}
            </div>
          )}

          <div className="py-1">
            {showProfileLink && (
              <Link
                to="/user-profile"
                // Closes when already on the profile page, where no route
                // change happens to close it.
                onClick={() => setOpen(false)}
                className="block px-4 py-3 text-slate-800 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none"
              >
                User profile
              </Link>
            )}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="block w-full px-4 py-3 text-left text-slate-800 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getInitials(profile: UserProfile | undefined): string {
  if (!profile) return '';
  const fromName = `${profile.givenName.charAt(0)}${profile.familyName.charAt(0)}`;
  if (fromName) return fromName.toUpperCase();
  return profile.email.charAt(0).toUpperCase();
}

function PersonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5.5 19a6.5 6.5 0 0113 0" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 12"
      aria-hidden="true"
      className={`hidden shrink-0 transition-transform md:block ${open ? 'rotate-180' : ''}`}
    >
      <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
